import React, { useState, useEffect, useContext } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { supabase } from '../../../supabaseConfig';
import { AppContext } from '../../../Context/AppContext';
import styles from './IdCardAd.module.css';
import paymentQr from '../../../assets/payment.png';
import { CheckCircle, XCircle, Clock, UploadCloud, ArrowRight } from 'lucide-react';

const IdCardAd = () => {
  const { user } = useContext(AppContext);
  const [step, setStep] = useState('form'); // 'form', 'payment'
  const [picture, setPicture] = useState(null);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [applicationData, setApplicationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    sysId: '',
    cnic: '',
    session: '',
    className: '',
  });

  useEffect(() => {
    if (user) {
      let programName = user.program || '';
      if (programName === 'BCS') {
        programName = 'BSCS';
      } else if (programName === 'BSE') {
        programName = 'BSSE';
      }
      setFormData(prev => ({ ...prev, className: programName }));
    }
  }, [user]);

  useEffect(() => {
    if (!user?.stdRegNumber) return;

    const checkApplication = async () => {
      setLoading(true);
      try {
        const appDocRef = doc(db, 'idCardApplications', user.stdRegNumber);
        const appDocSnap = await getDoc(appDocRef);
        if (appDocSnap.exists()) {
          const appData = appDocSnap.data();
          setApplicationStatus(appData.status);
          setApplicationData(appData);
        } else {
          setApplicationStatus('not_applied');
        }
      } catch (error) {
        console.error('Error checking application status:', error);
        setApplicationStatus('not_applied');
      }
      setLoading(false);
    };

    checkApplication();
  }, [user]);

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setPicture(e.target.files[0]);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTryAgain = () => {
    setApplicationStatus('not_applied');
    setStep('form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!picture || !formData.sysId || !formData.cnic || !formData.session || !formData.className) {
      alert('Please fill all fields and upload a picture.');
      return;
    }
    if (!user) {
      alert('User not found.');
      return;
    }

    setLoading(true);
    try {
      const fileExt = picture.name.split('.').pop();
      const fileName = `${user.stdRegNumber}_${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('id-cards')
        .upload(filePath, picture, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('id-cards').getPublicUrl(filePath);
      const pictureUrl = urlData.publicUrl;

      const appDocRef = doc(db, 'idCardApplications', user.stdRegNumber);
      await setDoc(appDocRef, {
        studentId: user.stdRegNumber,
        name: `${user.firstName} ${user.lastName}`,
        className: formData.className,
        department: user.department,
        sysId: formData.sysId,
        cnic: formData.cnic,
        session: formData.session,
        pictureUrl,
        status: 'pending',
        appliedAt: serverTimestamp(),
      }, { merge: true });

      setApplicationStatus('pending');
      setStep('payment');
    } catch (error) {
      console.error('Error submitting application:', error);
      alert(`Failed to submit application. Please try again.\nError: ${error.message}`);
    }
    setLoading(false);
  };

  const phoneNumber = '+923109233844';
  const whatsappLink = `https://wa.me/${phoneNumber}`;

  const renderStatus = (status) => {
    switch (status) {
      case 'pending':
        return (
          <div className={`${styles.statusBox} ${styles.pending}`}>
            <Clock size={48} />
            <h2>Application Submitted</h2>
            <p>Your eID card application is pending approval.</p>
            <div className={styles.paymentDetails}>
              <p>Please pay <strong>PKR 200/-</strong> to complete your application.</p>
              <img src={paymentQr} alt="Payment QR Code" className={styles.qrCode} />
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className={styles.whatsappButton}>
                Share Payment Screenshot on WhatsApp
              </a>
              <p className={styles.boldMessage}>
                Your application will be processed after payment confirmation.
              </p>
            </div>
          </div>
        );
      case 'approved':
        return (
          <div className={`${styles.statusBox} ${styles.approved}`}>
            <CheckCircle size={48} />
            <h2>Application Approved!</h2>
            <p>Your eID card is ready. You can now view it from your profile.</p>
          </div>
        );
      case 'rejected':
        return (
          <div className={`${styles.statusBox} ${styles.rejected}`}>
            <XCircle size={48} />
            <h2>Application Rejected</h2>
            {applicationData?.rejectionReason && <p>Reason: {applicationData.rejectionReason}</p>}
            <button onClick={handleTryAgain} className={styles.submitButton}>
              Try Again
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <div className={styles.adContainer}><div className={styles.adBox}><p>Loading...</p></div></div>;
  }

  if (applicationStatus && applicationStatus !== 'not_applied') {
    return (
      <div className={styles.adContainer}>
        {renderStatus(applicationStatus)}
      </div>
    );
  }

  return (
    <div className={styles.adContainer}>
      <div className={`${styles.adBox} ${step === 'form' ? styles.formStep : styles.paymentStep}`}>
        {step === 'form' ? (
          <form onSubmit={handleSubmit} className={styles.form}>
            <h2>Apply for eID Card</h2>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label>Registration Number</label>
                <input type="text" value={user?.stdRegNumber || ''} disabled />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="className">Program/Class</label>
                <input type="text" id="className" name="className" value={formData.className} onChange={handleFormChange} disabled />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="sysId">SYS ID</label>
                <input type="text" id="sysId" name="sysId" value={formData.sysId} onChange={handleFormChange} required />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="cnic">CNIC #</label>
                <input type="text" id="cnic" name="cnic" value={formData.cnic} onChange={handleFormChange} required />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="session">Session</label>
                <select id="session" name="session" value={formData.session} onChange={handleFormChange} required>
                  <option value="">Select Session</option>
                  <option value="Sep-2025 to Aug-2029">Sep-2025 to Aug-2029</option>
                  <option value="Feb-2025 to Jan-2029">Feb-2025 to Jan-2029</option>
                  <option value="Sep-2024 to Aug-2028">Sep-2024 to Aug-2028</option>
                  <option value="Feb-2024 to Jan-2028">Feb-2024 to Jan-2028</option>
                  <option value="Sep-2023 to Aug-2027">Sep-2023 to Aug-2027</option>
                  <option value="Feb-2023 to Jan-2027">Feb-2023 to Jan-2027</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <br/>
                <label htmlFor="picture" className={styles.uploadLabel}>
                  <UploadCloud size={20} />
                  <span>{picture ? picture.name : 'Upload Your Picture'}</span>
                </label>
                <input type="file" id="picture" accept="image/*" onChange={handleFileChange} required />
              </div>
            </div>
            <button type="submit" className={styles.submitButton} disabled={loading}>
              {loading ? 'Submitting...' : 'Submit & Proceed'}
              <ArrowRight size={20} />
            </button>
          </form>
        ) : (
          <div className={styles.paymentDetails}>
            <h2>Payment</h2>
            <p>Please pay <strong>PKR 200/-</strong> to complete your application.</p>
            <img src={paymentQr} alt="Payment QR Code" className={styles.qrCode} />
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className={styles.whatsappButton}>
              Share Payment Screenshot on WhatsApp
            </a>
            <p className={styles.boldMessage}>
              Your application will be processed after payment confirmation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IdCardAd;
